// UK formatting utilities for dates and currency

/**
 * Formats a date to UK standard (DD/MM/YYYY)
 * @param {string|Date} dateString - Date string or Date object
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} - Formatted date string
 */
export const formatUKDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/London'
    };
    
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = false; // Use 24-hour format
    }
    
    return date.toLocaleString('en-GB', options);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Formats a date/time to UK standard with time (DD/MM/YYYY, HH:mm)
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} - Formatted date and time string
 */
export const formatUKDateTime = (dateString) => {
  return formatUKDate(dateString, true);
};

/**
 * Formats currency to UK standard (£X.XX)
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code (defaults to GBP)
 * @returns {string} - Formatted currency string
 */
export const formatUKCurrency = (amount, currency = 'GBP') => {
  if (amount === null || amount === undefined || amount === '') return '£0.00';
  
  try {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '£0.00';
    
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numAmount);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  }
};

/**
 * Formats time only to UK standard (HH:mm)
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} - Formatted time string
 */
export const formatUKTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/London'
    });
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'N/A';
  }
};